<template>
  <PageHeader :showBreadcrumb="true" />
  <!-- Button to Trigger PDF Generation -->
  <button @click="generatePDF">Generate PDF</button>
  <div id="pdf-content">
    <!-- Patient Section -->
    <fieldset>
      <legend>Patient Information</legend>

      <MnRow>
        <MnColumn cols="6">
          <Panel
            id="firstName"
            header="First Name"
          >{{appealData.patient.firstName}}</Panel>
        </MnColumn>
        <MnColumn cols="6">
          <Panel
            id="middleName"
            header="Middle Name"
          >{{appealData.patient.middleName}}</Panel>
        </MnColumn>
        <MnColumn cols="6">
          <Panel
            id="lastName"
            header="Last Name"
          >{{appealData.patient.lastName}}</Panel>
        </MnColumn>
      </MnRow>

      <MnRow>
        <MnColumn cols="6">
          <Panel
            id="dob"
            header="Date of Birth"
          >{{new Date(appealData.patient.dob).toLocaleDateString()}}</Panel>
        </MnColumn>
        <MnColumn cols="6">
          <Panel
            id="phoneNumber"
            header="Phone Number"
          >{{appealData.patient.phoneNumber}}</Panel>
        </MnColumn>
        <MnColumn cols="6">
          <Panel
            id="email"
            header="Email"
          >{{appealData.patient.email}}</Panel>
        </MnColumn>
      </MnRow>

      <MnRow>
        <MnColumn cols="6">
          <Panel
            id="relationshipToPatient"
            header="Relation to the Patient"
          >{{appealData.patient.relationshipToPatient}}</Panel>
        </MnColumn>

      </MnRow>
    </fieldset>
    <fieldset>
      <MnRow>
        <MnColumn cols="4">
          <Panel
            id="isExpedited"
            header="Is Expedited"
          >{{(appealData.isExpedited)?'Yes':'No'}}</Panel>
        </MnColumn>
        <MnColumn cols="4">
          <Panel v-if="appealData.isExpedited"
                 id="reasonExpedited"
                 header="Expedited Reason"
          >{{appealData.expeditedReason}}</Panel>
        </MnColumn>
      </MnRow>
    </fieldset>

    <br/>

    <fieldset>
      <legend>Patient Address</legend>
      <MnRow>
        <MnColumn cols="8">
          <Panel
            id="street"
            header="Street"
          >{{appealData.patient.street}}</Panel>
        </MnColumn>
      </MnRow>

      <MnRow>
        <MnColumn cols="4">
          <Panel
            id="city"
            header="City"
          >{{appealData.patient.city}}</Panel>
        </MnColumn>

        <MnColumn cols="4">
          <Panel
            id="state"
            header="State"
          >{{appealData.patient.state}}</Panel>
        </MnColumn>

        <MnColumn cols="4">
          <Panel
            id="zipCode"
            header="Zip Code"
          >{{appealData.patient.zipCode}}</Panel>
        </MnColumn>
      </MnRow>
    </fieldset>

    <br/>

    <fieldset>
      <legend>Health Plan Information</legend>

      <MnRow>
        <MnColumn cols="4">
          <Panel
            v-if="appealData.healthPlan.healthInsuranceCompany !== EXTERNAL_APPEALS_HEALTH_INSURANCE_COMPANIES.Other"
            id="healthInsuranceCompany"
            header="Health Insurance Company"
          >{{appealData.healthPlan.healthInsuranceCompany}}</Panel>
        </MnColumn>

        <MnColumn cols="4">
          <Panel v-if="appealData.healthPlan.healthInsuranceCompany === EXTERNAL_APPEALS_HEALTH_INSURANCE_COMPANIES.Other"
                 id="otherHmoName"
                 header="Other Health Insurance Company Name"
          >{{appealData.healthPlan.otherHmoName}}</Panel>
        </MnColumn>

        <MnColumn cols="4">
          <Panel
            id="patientInsuranceId"
            header="Patient Insurance ID"
          >{{appealData.healthPlan.patientInsuranceId}}</Panel>
        </MnColumn>
      </MnRow>

      <MnRow>
        <MnColumn cols="4">
          <Panel
            id="typeOfCoverage"
            header="Type of Coverage"
          >{{appealData.healthPlan.typeOfCoverage}}</Panel>
        </MnColumn>

        <MnColumn cols="4">
          <Panel
            v-if="appealData.healthPlan.otherTypeOfCoverage !== null"
            id="otherHmoName"
            header="Other Type of Coverage"
          >{{appealData.healthPlan.otherTypeOfCoverage}}</Panel>
        </MnColumn>
      </MnRow>
    </fieldset>

    <br/>

    <fieldset>
      <legend>Summary of Appeal</legend>
      <MnRow>
        <MnColumn cols="12">
          <Panel
            id="summaryOfAppeal"
            header="Summary of Appeal"
          >{{appealData.summaryOfAppeal}}</Panel>
        </MnColumn>
      </MnRow>
    </fieldset>
    <fieldset>
      <legend>Signature, Attestation and Consent to Release Information</legend>
      <p>I certify that all the information in this form is true to the best of my knowledge. I am insured by the above health plan company.
        To go through an external appeal, I understand that I need to have gone through my health plan company’s internal appeal process.
        I authorize my health plan company and my medical providers to release my medical records to the Minnesota Department of Health solely for the purpose of reviewing my complaint.
        This consent will be revoked upon the conclusion of the investigation or external appeal.
        I authorize the Minnesota Department of Health to forward any documents connected to my external appeal to a state-approved IRO contract vendor.</p>
      <p>I understand it may be necessary for MDH to obtain copies of protected health information for MDH to fully investigate my complaint.
        My signature below is my signed permission allowing MDH to obtain this information.</p>
      <p>If an authorized representative is designated above, that person is authorized to represent me in the external appeal process.</p>
      <p>By typing my name in the signature field, I understand that I am electronically signing this form. I attest and certify that the information provided is true and accurate.
        I understand that my electronic signature has the same legal effect and can be enforced in the same way as a handwritten signature. (MN Statutes, Chapter 325L.07)</p>

      <MnRow>
        <MnColumn cols="6">
          <Panel
              id="signature"
              header="Signature"
          >{{appealData.signature}}</Panel>
        </MnColumn>
        <MnColumn cols="6">
          <Panel
              id="signatureDate"
              header="Signature Date"
          >{{appealData.signatureDate}}</Panel>
        </MnColumn>
      </MnRow>
    </fieldset>
  </div>
</template>
<script>

import {getExternalAppealById} from "@/services/externalAppealService.js";
import {Panel} from "primevue";
import MnRow from "@/components/common/MnRow.vue";
import MnColumn from "@/components/common/MnColumn.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import {useAuthStore} from "@/stores/authStore.js";
import html2pdf from "html2pdf.js";
import {ExternalAppealModel} from "@/models/external_appeals/ExternalAppealModel.js";
import {EXTERNAL_APPEALS_HEALTH_INSURANCE_COMPANIES} from "@/utils/constants.js";




export default {
  computed: {
    EXTERNAL_APPEALS_HEALTH_INSURANCE_COMPANIES() {
      return EXTERNAL_APPEALS_HEALTH_INSURANCE_COMPANIES;
    }
  },
  components: {PageHeader, useAuthStore, Panel, MnRow, MnColumn},
  data(){
    return {
      appealData: new ExternalAppealModel(),
      appealId: null
    };
  },
  async beforeMount() {
    this.appealId = this.$route.query.appealId;
    this.appealData = await getExternalAppealById(this.appealId);
    this.appealData.signatureDate = new Date(this.appealData.signatureDate).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
    this.appealData.patient.dob = new Date(this.appealData.patient.dob).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });


  },
  methods: {
    generatePDF() {
      // Ask the user for a file name
      let fileName = prompt("Enter file name for appeal pdf:", "external-appeal");

      // Validate filename
      if (!fileName || !fileName.trim()) {
        alert("Invalid file name. Download canceled.");
        return;
      }

      //clean file name of any file type if user added one
      if(fileName.lastIndexOf('.')!==-1){
        let lastIndex = fileName.lastIndexOf('.');
        fileName = fileName.slice(0,lastIndex);
      }

      const element = document.getElementById("pdf-content");
      const options = {
        margin: 1,
        filename: fileName,
        html2canvas: {scale: 2},
        jsPDF: {unit: "in", format: "letter", orientation: "portrait"},
      };
      html2pdf().set(options).from(element).save();



    },
  },

};
</script>